import type { JourneyPath, Lesson, Module } from '../types/journey';

/**
 * Launch scaffolds for courses that are published in Sanity but have no
 * usable lessons. English only. Short practical guidance for newcomers.
 * Sanity body content is kept when it already exists; these lessons fill gaps.
 */

type Block = {
  _type: 'block';
  _key: string;
  style: 'normal';
  markDefs: [];
  children: { _type: 'span'; _key: string; text: string; marks: string[] }[];
};

let blockKey = 0;

function textBlock(text: string): Block {
  const id = `lb${blockKey++}`;
  return {
    _type: 'block',
    _key: id,
    style: 'normal',
    markDefs: [],
    children: [{ _type: 'span', _key: `${id}s`, text, marks: [] }],
  };
}

function blocks(lines: string[]): Block[] {
  return lines.map(textBlock);
}

interface ReadingInput {
  id: string;
  title: string;
  slug: string;
  order: number;
  duration: string;
  skill: string;
  intro: string;
  paragraphs: string[];
  canDo: string[];
  takeaways: string[];
  task: string;
}

export function readingLesson(input: ReadingInput): Lesson {
  return {
    _id: input.id,
    title: input.title,
    slug: input.slug,
    order: input.order,
    type: 'reading',
    duration: input.duration,
    targetAudience: 'all',
    yourKeySkill: input.skill,
    introduction: blocks([input.intro]),
    content: blocks(input.paragraphs),
    keyTakeaways: blocks(input.takeaways),
    measurableDeliverables: input.canDo.map((description, index) => ({
      _key: `${input.id}-can-${index}`,
      inputType: 'display' as const,
      description,
      isOptional: false,
    })),
    actionableTasks: [
      {
        _key: `${input.id}-task`,
        inputType: 'checkbox' as const,
        description: input.task,
        isOptional: false,
      },
    ],
  };
}

function module(id: string, title: string, description: string, order: number, lessons: Lesson[]): Module {
  return {
    _id: id,
    title,
    description,
    order,
    targetAudience: 'all',
    isPremium: false,
    lessons,
  };
}

/**
 * Lessons that exist in Sanity but have no body. Keyed by lesson slug.
 * Applied only when the published lesson is missing that field.
 */
export const LESSON_CONTENT_PATCHES: Record<string, Partial<Lesson>> = {
  'quiz-1': {
    yourKeySkill: 'Check what you remember before you move on',
    introduction: blocks([
      'Pause and check the basics from orientation: who to call in an emergency, and one custom that will help you on an ordinary day.',
    ]),
    content: blocks([
      'This is a short check, not a test you can fail. If you miss one, go back to the earlier lesson and read it again. The goal is to leave orientation knowing what to do in the first hour of a problem.',
      'Keep answers practical. You do not need perfect English. You need a number you can dial and a habit you can use this week.',
    ]),
    keyTakeaways: blocks([
      '911 is for emergencies: police, fire, and medical help. It is free to call.',
      'If you are unsure, review the orientation lessons and then retake this check.',
    ]),
    measurableDeliverables: [
      {
        _key: 'quiz-1-can-0',
        inputType: 'display',
        description: 'Name the emergency number and one reason you would call it.',
        isOptional: false,
      },
    ],
    actionableTasks: [
      {
        _key: 'quiz-1-task',
        inputType: 'checkbox',
        description: 'Save 911 in your phone and write one sentence about when you would use it.',
        isOptional: false,
      },
    ],
    associatedQuiz: {
      _id: 'quiz-1-check',
      title: 'Orientation check',
      slug: 'orientation-check',
      questions: [
        {
          question: 'Which number do you call for police, fire, or medical emergencies in the United States?',
          options: ['311', '411', '911', '811'],
          correctAnswer: 2,
          explanation: '911 reaches emergency services. 311 is often for non-emergency city services, and it is not available everywhere.',
        },
        {
          question: 'A friend says "How are you?" in passing at the store. What is a normal reply?',
          options: [
            'Give your full medical history',
            'A short reply such as "Good, thanks" is enough',
            'Ignore them; the question is a trick',
            'Ask to see their ID',
          ],
          correctAnswer: 1,
          explanation: 'In everyday American English, "How are you?" is often a greeting, not a request for a long answer.',
        },
      ],
    },
  },
  'setting-up-your-phone': {
    title: 'Setting Up Your Phone',
    yourKeySkill: 'Get a U.S. phone number you can use for banks, jobs, and verification codes',
    introduction: blocks([
      'A U.S. phone number is how banks, employers, and schools confirm it is really you. Set one up in your first days, even if you keep your home-country phone for family.',
    ]),
    content: blocks([
      'You can start with a prepaid plan from a carrier store or a big retailer. Prepaid means you pay ahead and usually do not need a credit check. Postpaid plans bill you monthly and often ask for a Social Security number and a credit check, which many newcomers do not have yet.',
      'Bring your passport, a second ID if you have one, and the address where you are staying. Ask whether the plan includes a U.S. number, hotspot data, and international calls to your family. Compare the total price, not just the advertised monthly rate.',
      'Use the new number on your bank, email, and job applications. Turn on a screen lock. Do not share verification codes with anyone who calls you, even if they say they are from the bank or the government.',
    ]),
    keyTakeaways: blocks([
      'A prepaid U.S. number is a solid first step when you do not have credit yet.',
      'Never share one-time codes. Real banks do not ask you to read them aloud to a caller.',
    ]),
    measurableDeliverables: [
      {
        _key: 'phone-can-0',
        inputType: 'display',
        description: 'Choose a prepaid or postpaid plan and say why it fits your first month.',
        isOptional: false,
      },
    ],
    actionableTasks: [
      {
        _key: 'phone-task',
        inputType: 'checkbox',
        description: 'Write down two phone plans, their monthly price, and whether they require a credit check.',
        isOptional: false,
      },
    ],
  },
  intenet: {
    title: 'Internet at Home',
    yourKeySkill: 'Get online without signing a contract you cannot leave',
    introduction: blocks([
      'You will need internet for job applications, school, maps, and video calls home. You do not have to sign a long contract on day one.',
    ]),
    content: blocks([
      'A phone hotspot can cover the first week. It is slower for several people, but it works while you learn the neighborhood. Public libraries also offer free Wi-Fi and computers.',
      'Before you order home internet, ask the landlord what is already included and which companies serve the building. Ask about the price after any promo ends, the equipment fee, and the early-cancellation fee. A no-contract or month-to-month plan is safer if you might move.',
      'Use the address on your lease or a letter you can show. Installation may ask for the name of an adult on the account. Keep the confirmation email. That account name and address will also help when you open a bank account.',
    ]),
    keyTakeaways: blocks([
      'Library Wi-Fi and a phone hotspot are enough while you compare home plans.',
      'Ask for the price after the promotion and the fee to cancel.',
    ]),
    measurableDeliverables: [
      {
        _key: 'internet-can-0',
        inputType: 'display',
        description: 'Name one temporary way to get online and one home plan to compare.',
        isOptional: false,
      },
    ],
    actionableTasks: [
      {
        _key: 'internet-task',
        inputType: 'checkbox',
        description: 'Write the address you will use and one internet provider you will call or check online.',
        isOptional: false,
      },
    ],
  },
  'lesson-0-4-navigating-cultural-differences': {
    content: blocks([
      'Small habits make daily life easier. People often say "How are you?" as a hello. A short answer is enough. Wait your turn in lines, and leave some personal space in conversation.',
      'At restaurants, a tip of about 15 to 20 percent is expected when you sit down and a server takes care of the table. Fast-food counters usually do not expect a tip. If a custom is unclear, it is normal to ask, "How does this usually work here?"',
      'Asking for help is not rude. Neighbors, librarians, and community centers expect newcomers to have questions. You do not need perfect English to start.',
    ]),
    yourKeySkill: 'Handle everyday customs without guessing in the moment',
    keyTakeaways: blocks([
      '"How are you?" is often just a greeting.',
      'Sit-down restaurants usually expect a 15–20% tip. Asking how something works is welcome.',
    ]),
  },
};

export const PLACEHOLDER_LESSON_SLUGS = new Set([
  'lesson-0-1-introduction-1',
  'lesson-0-1-introduction-2',
]);

const budgetingModules: Module[] = [
  module(
    'local-budget-m1',
    'See where money goes',
    'A first picture of income and costs in the U.S., before you try to save.',
    1,
    [
      readingLesson({
        id: 'local-budget-l1',
        title: 'What a first U.S. budget includes',
        slug: 'first-us-budget',
        order: 1,
        duration: '12 min',
        skill: 'Name the bills that have to be paid before anything else',
        intro: 'A budget is a plan for the money you already have. It is not a test of whether you earn enough.',
        paragraphs: [
          'Start with money coming in this month: pay after tax, help from family, or savings you brought. Write the number you can actually spend, not the salary before taxes.',
          'Then list needs: rent, food, a phone, transport, and any debt payment. In many cities, rent is the largest bill. A common guide is to keep rent near a third of take-home pay, but the real rule is: you must be able to pay it every month.',
          'Put wants in a separate list: eating out, new clothes, sending extra money home. You can still do those things. The budget just shows what is left after needs.',
        ],
        canDo: ['Separate needs from wants for one month', 'Write down take-home pay, not the pre-tax number'],
        takeaways: [
          'Budget with the money that reaches your account.',
          'Rent, food, phone, and transport come before wants.',
        ],
        task: 'Write this month’s take-home pay and your four largest needs.',
      }),
      readingLesson({
        id: 'local-budget-l2',
        title: 'Track one week of spending',
        slug: 'track-one-week',
        order: 2,
        duration: '10 min',
        skill: 'Notice small costs that add up',
        intro: 'You cannot fix a budget you have not seen. One week of notes is enough to start.',
        paragraphs: [
          'For seven days, write every purchase: cash, debit card, and apps. Include the $3 coffee and the bus fare. Use a notes app or a piece of paper. The tool does not matter.',
          'At the end of the week, group the lines into food, transport, phone, and other. Circle anything that surprised you. That surprise is the point of tracking.',
          'Do not judge the week. If money is tight, the next lesson turns this list into a plan. If you share costs with family, track only what you personally pay, then add shared bills once.',
        ],
        canDo: ['Keep a seven-day spending list', 'Group spending into a few categories'],
        takeaways: [
          'Track cash and cards together or the picture will be wrong.',
          'Look for surprises, not perfection.',
        ],
        task: 'Start a seven-day list today and add the first three things you already paid for.',
      }),
    ]
  ),
  module(
    'local-budget-m2',
    'Make a first-month plan',
    'Turn the list into a simple plan you can follow this month.',
    2,
    [
      readingLesson({
        id: 'local-budget-l3',
        title: 'Pay needs first, then choose wants',
        slug: 'needs-then-wants',
        order: 1,
        duration: '12 min',
        skill: 'Decide what gets paid on payday',
        intro: 'Payday is the moment to move money to needs before it disappears on small purchases.',
        paragraphs: [
          'On the day you are paid, pay or set aside rent, utilities you owe, transport, and a food amount. Many people move rent money to a separate savings bucket the same day so it is not spent by accident.',
          'Food can be a weekly amount instead of one big number. Groceries are usually cheaper than eating out. A simple lunch from home a few days a week changes the month more than skipping one coffee.',
          'Money you send to family is a real bill for many newcomers. Put it in the plan on purpose, with an amount you can repeat. A smaller regular amount is safer than a large one you cannot sustain.',
        ],
        canDo: ['Assign payday money to needs before wants', 'Choose a weekly food amount'],
        takeaways: [
          'Move rent money aside on payday.',
          'Treat support for family as a planned amount, not a leftover.',
        ],
        task: 'Write the order you will pay bills on your next payday, starting with housing.',
      }),
      readingLesson({
        id: 'local-budget-l4',
        title: 'Start a small emergency cushion',
        slug: 'small-emergency-cushion',
        order: 2,
        duration: '10 min',
        skill: 'Keep a little cash ready for a surprise bill',
        intro: 'An emergency cushion is money you do not spend on ordinary days. It can start very small.',
        paragraphs: [
          'A useful first target is $200 to $500, enough for a transit card, a clinic copay, or a few days of food if a paycheck is late. You do not need three months of expenses before you begin.',
          'Each payday, move a fixed small amount — even $10 or $20 — into a savings account or an envelope you do not touch. Automate it if the bank lets you. Skipping a week is better than quitting.',
          'Use the cushion only for surprises: a late bus that forces a rideshare is not an emergency, but a broken phone you need for work can be. When you use it, start filling it again the next payday.',
        ],
        canDo: ['Pick a first savings target under $500', 'Choose an amount to set aside each payday'],
        takeaways: [
          'A small cushion you actually keep is better than a perfect plan you do not start.',
          'Refill the cushion after you use it.',
        ],
        task: 'Choose a first target amount and the dollar amount you will move on payday.',
      }),
    ]
  ),
];

const healthModules: Module[] = [
  module(
    'local-health-m1',
    'How coverage works',
    'The words and the places coverage comes from, in plain language.',
    1,
    [
      readingLesson({
        id: 'local-health-l1',
        title: 'The four words on every insurance card',
        slug: 'insurance-words',
        order: 1,
        duration: '12 min',
        skill: 'Read a plan well enough to avoid a surprise bill',
        intro: 'U.S. health care is expensive without insurance. You do not need to understand the whole system. You need four words.',
        paragraphs: [
          'Premium is what you pay every month to keep the plan, even if you do not see a doctor. Deductible is what you pay yourself each year before the plan starts paying for many services. Copay is a fixed amount for a visit, such as $30. Coinsurance is a percentage you pay after the deductible.',
          'Network means the doctors and clinics that have a contract with your plan. Staying in network usually costs much less. Out of network can mean you pay most of the bill yourself.',
          'Before a non-urgent visit, ask: "Do you take my plan?" and "Is this in network?" Write down the answer and the person’s name. Urgent danger is different: call 911 or go to an emergency room if someone may die or be seriously hurt. You can sort out insurance after you are safe.',
        ],
        canDo: ['Explain premium, deductible, copay, and network', 'Ask whether a clinic is in network'],
        takeaways: [
          'The monthly premium does not mean visits are free.',
          'In-network care is usually far cheaper. Emergencies still come first.',
        ],
        task: 'Write the four words and one question you will ask before a clinic visit.',
      }),
      readingLesson({
        id: 'local-health-l2',
        title: 'Where people actually get a plan',
        slug: 'where-coverage-comes-from',
        order: 2,
        duration: '12 min',
        skill: 'Find the door that matches your situation',
        intro: 'Most people get coverage from a job, a state program, or HealthCare.gov. The right door depends on income, immigration status, and whether an employer offers a plan.',
        paragraphs: [
          'If an employer offers insurance, ask human resources when you can enroll and what part of the premium you pay. Some jobs make you wait 30 to 90 days. Ask whether dental and vision are separate.',
          'HealthCare.gov is the federal marketplace. Some states use their own site. Open enrollment is once a year, but a move, a job loss, or certain other life changes can open a special enrollment period. Medicaid and the Children’s Health Insurance Program (CHIP) are state programs for people with lower income, including many children and pregnant people. Rules depend on the state and on immigration status.',
          'Community health centers provide care on a sliding scale if you are uninsured. They are a real option while you sort out a plan. Bring any ID you have, a list of medicines, and a way to be contacted.',
        ],
        canDo: ['Name the coverage door that fits your job and family', 'Know that a community health center can see you while you wait'],
        takeaways: [
          'Ask your employer first if you have a job with benefits.',
          'A community health center can bridge a gap. Do not wait for a perfect plan to get urgent care.',
        ],
        task: 'Write which door you will check first: employer, HealthCare.gov, Medicaid, or a community health center.',
      }),
    ]
  ),
  module(
    'local-health-m2',
    'Use care without a surprise bill',
    'Choose the right place to go, and bring what the desk will ask for.',
    2,
    [
      readingLesson({
        id: 'local-health-l3',
        title: 'Clinic, urgent care, or emergency room',
        slug: 'where-to-go-for-care',
        order: 1,
        duration: '10 min',
        skill: 'Pick the lowest-cost place that is still safe',
        intro: 'The emergency room is for emergencies. Using it for a sore throat can cost hundreds or thousands of dollars.',
        paragraphs: [
          'A primary care clinic or community health center is for checkups, ongoing problems, vaccines, and referrals. Urgent care is for problems that need help today but are not life-threatening, such as a painful ear, a sprain, or a fever. The emergency room and 911 are for chest pain, trouble breathing, heavy bleeding, a serious injury, or any time you think a life is in danger.',
          'If you have a plan, look up urgent care that is in network before you need it. Save the address in your phone. If a nurse line is on your card, call it when you are unsure. They can tell you where to go.',
          'After the visit, keep the paperwork. If a bill looks wrong, call the number on the bill and ask for an itemized list before you pay a large amount. Ask if they have financial assistance.',
        ],
        canDo: ['Match a problem to clinic, urgent care, or emergency care', 'Save one in-network urgent care address'],
        takeaways: [
          'Emergency rooms are for danger, not for convenience.',
          'Look up an in-network urgent care before you are sick.',
        ],
        task: 'Save 911 and one clinic or urgent care address in your phone.',
      }),
      readingLesson({
        id: 'local-health-l4',
        title: 'What to bring to an appointment',
        slug: 'appointment-checklist',
        order: 2,
        duration: '8 min',
        skill: 'Walk into the visit with the papers and questions you need',
        intro: 'The front desk will ask who you are, how you will pay, and why you came. A small folder makes this calm.',
        paragraphs: [
          'Bring a photo ID, your insurance card if you have one, a list of medicines and doses, and any records you carried from home. If a family member is translating, ask them to come. You can also ask the clinic for an interpreter. You have a right to understand your care.',
          'Write two questions before you go. Examples: "What is this medicine for?" and "What should I do if I feel worse tonight?" Ask what the visit will cost if you are uninsured. Sliding-scale clinics can lower the fee based on income.',
          'Leave with the next step written down: a prescription, a follow-up date, or a phone number. If you do not understand, say "Please explain that again more simply." That is a complete sentence, and it is enough.',
        ],
        canDo: ['Pack ID, medicine list, and two questions', 'Ask for an interpreter or a simpler explanation'],
        takeaways: [
          'A medicine list and two questions change the quality of the visit.',
          'You can ask for an interpreter and for simpler words.',
        ],
        task: 'Make a note with your medicines and two questions you would ask a doctor.',
      }),
    ]
  ),
];

function journeyShell(
  id: string,
  title: string,
  slug: string,
  duration: string,
  isPremium: boolean,
  summary: string,
  modules: Module[]
): JourneyPath {
  return {
    _id: id,
    title,
    slug,
    duration,
    level: 'beginner',
    isPremium,
    rating: 4.8,
    enrolled: 0,
    targetAudience: 'all',
    shortDescription: summary,
    introduction: blocks([summary]),
    modules,
  };
}

export const LOCAL_SHIPPABLE_JOURNEYS: JourneyPath[] = [
  journeyShell(
    'local-budgeting-start',
    'Budgeting for a Fresh Start',
    'budgeting-start',
    '2–3 weeks',
    false,
    'A short free path: see where your money goes, pay needs first, and start a small cushion.',
    budgetingModules
  ),
  journeyShell(
    'local-health-insurance',
    'Health Insurance Basics',
    'health-insurance',
    '3–4 weeks',
    false,
    'A short free path: the words on an insurance card, where coverage comes from, and where to go when you are sick.',
    healthModules
  ),
];

/**
 * Used only when Sanity cannot be reached, so Guides still lists the real catalog
 * instead of a blank page. Published Sanity records replace these titles when the API works.
 */
export const OFFLINE_INCOMPLETE_JOURNEYS: JourneyPath[] = [
  journeyShell('offline-renting', 'Renting, Banking & ID Setup', 'renting-id-setup', '4–5 weeks', true, 'Published with no modules yet.', []),
  journeyShell('offline-credit', 'Build Your Credit Foundation', 'build-your-credit-foundation', '3–4 weeks', true, 'Published with no modules yet.', []),
  journeyShell('offline-golden', 'Preparing for Your Golden Years', 'preparing-for-your-golden-years', '3 hours', true, 'One module with a lesson that has no body content.', []),
  journeyShell('offline-self', 'Self-Employment & Side Income', 'self-employment', '6–8 weeks', true, 'Published with no modules yet.', []),
  journeyShell('offline-taxes', 'Understanding U.S. Taxes', 'us-taxes', '4–5 weeks', true, 'Published with no modules yet.', []),
];

export const LAUNCH_INVENTORY = [
  {
    slug: 'new-to-america',
    title: 'New to America: Your Essential Guide',
    sanity: 'partial',
    launch: 'shippable',
    evidence:
      'Sanity has 9 modules. Only Welcome & Orientation has several real lessons. Arrival lessons "Setting Up Your Phone" and "Intenet" are empty shells and are filled here. Quiz 1 has no body and is filled with a 2-question check. Lesson 0.4 has an intro only; a short body is added. Duplicate placeholders lesson-0-1-introduction-1 and introduction-2 are hidden. Empty modules (healthcare, both education tracks, financial literacy, community, non-immigrant documents) are hidden so they cannot be started.',
  },
  {
    slug: 'budgeting-start',
    title: 'Budgeting for a Fresh Start',
    sanity: 'empty',
    launch: 'shippable',
    evidence: 'Sanity journey has zero modules. Local scaffold adds 2 modules and 4 short lessons so a free user can finish the path.',
  },
  {
    slug: 'health-insurance',
    title: 'Health Insurance Basics',
    sanity: 'empty',
    launch: 'shippable',
    evidence: 'Sanity journey has zero modules. Local scaffold adds 2 modules and 4 short lessons.',
  },
  {
    slug: 'renting-id-setup',
    title: 'Renting, Banking & ID Setup',
    sanity: 'empty',
    launch: 'gated',
    evidence: 'Premium. Zero modules in Sanity. Shown as Coming Soon. Not started.',
  },
  {
    slug: 'build-your-credit-foundation',
    title: 'Build Your Credit Foundation',
    sanity: 'empty',
    launch: 'gated',
    evidence: 'Premium. Zero modules in Sanity. The credit lesson that does exist lives under New to America and stays available there.',
  },
  {
    slug: 'preparing-for-your-golden-years',
    title: 'Preparing for Your Golden Years',
    sanity: 'partial',
    launch: 'gated',
    evidence: 'Premium. One module, two lessons. "What is an IRA?" has a one-line intro and no body. Not treated as complete.',
  },
  {
    slug: 'self-employment',
    title: 'Self-Employment & Side Income',
    sanity: 'empty',
    launch: 'gated',
    evidence: 'Premium. Zero modules in Sanity.',
  },
  {
    slug: 'us-taxes',
    title: 'Understanding U.S. Taxes',
    sanity: 'empty',
    launch: 'gated',
    evidence: 'Premium. Zero modules in Sanity.',
  },
] as const;
